/**
 * useAutoScroll — scroll-position-aware auto-scroll for streaming chat.
 *
 * Tail-follows new content only while the user is "stuck" to the bottom.
 * The moment the user signals intent to read earlier content (wheel up,
 * touch drag, scrollbar drag, keyboard nav), tail-follow disengages
 * immediately and stays off until they return to the bottom themselves or
 * click the "↓ Jump to latest" pill bound to `scrollToBottom`.
 *
 * Programmatic scrolls are told apart from user scrolls by POSITION, not
 * by counting scroll events: every scroll we issue records its target
 * scrollTop, and the scroll listener treats an event as ours only when the
 * position matches that target. (The previous event-counter approach lost
 * sync whenever a programmatic set didn't change scrollTop — no event
 * fires — and then swallowed a real user scroll as "expected", which is
 * exactly what caused the mid-stream bounce-back.)
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const NEAR_BOTTOM_THRESHOLD = 80; // px
const SCROLLING_IDLE_MS = 1000; // hide scrollbar this long after last scroll event
const PROGRAMMATIC_TOLERANCE = 2; // px — scrollTop match window for "our" scrolls

// The chat messages stream is followed by a min-h-screen bottom spacer
// (so pin-to-top has scroll headroom). That spacer inflates scrollHeight
// by ~1 viewport, so any "distance to scrollHeight" math reports phantom
// room below the actual content — the "↓ Jump to latest" pill would always
// show even with the latest content fully visible. Compute the real
// content bottom from the last [data-chat-message-id] element instead.
const getContentBottom = (el: HTMLElement): number => {
  const messageEls = el.querySelectorAll<HTMLElement>('[data-chat-message-id]');
  const lastMsg = messageEls[messageEls.length - 1];
  if (lastMsg) {
    return lastMsg.offsetTop - el.offsetTop + lastMsg.offsetHeight;
  }
  return el.scrollHeight;
};

export function useAutoScroll<T extends HTMLElement>(deps: unknown[]) {
  const ref = useRef<T | null>(null);
  // isAtBottom drives the "↓ Jump to latest" pill (must be reactive state).
  const [isAtBottom, setIsAtBottom] = useState(true);

  // The single tail-follow decision. True = follow new content to the
  // bottom. Flipped off by user intent (wheel/touch/keys/scrollbar-up) or
  // pinMessageToTop; flipped on when the user reaches the bottom again or
  // clicks the pill.
  const stickToBottomRef = useRef(true);
  // Target of the most recent programmatic scroll — lets the scroll
  // listener classify events by position instead of by count.
  const lastProgrammaticTargetRef = useRef<number | null>(null);
  const lastScrollTopRef = useRef(0);
  // ≤1 tail-follow scroll per animation frame during token streaming.
  const rafIdRef = useRef<number | null>(null);

  // Scroll so the last message's bottom edge aligns with the viewport's
  // bottom edge, recording the target so the listener knows it was us.
  const performScrollToContentBottom = useCallback((el: HTMLElement) => {
    const top = Math.max(0, getContentBottom(el) - el.clientHeight);
    lastProgrammaticTargetRef.current = top;
    el.scrollTop = top;
  }, []);

  // Listeners: user-intent disengage + position tracking + scrollbar fade.
  useEffect(() => {
    const el = ref.current;
    if (!el) {return;}
    let idleTimer: ReturnType<typeof setTimeout> | null = null;
    const markScrollbarActive = () => {
      el.dataset.scrolling = 'true';
      if (idleTimer) {clearTimeout(idleTimer);}
      idleTimer = setTimeout(() => {
        el.dataset.scrolling = 'false';
      }, SCROLLING_IDLE_MS);
    };

    // Immediate disengage on read intent — fires BEFORE the next token's
    // tail-follow effect can move the view, so the user always wins.
    const disengage = () => {
      stickToBottomRef.current = false;
    };
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) {disengage();}
    };
    const onTouchMove = () => {
      disengage();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp' || e.key === 'PageUp' || e.key === 'Home') {
        disengage();
      }
    };

    const onScroll = () => {
      markScrollbarActive();
      const st = el.scrollTop;
      const target = lastProgrammaticTargetRef.current;
      const isOurs =
        target !== null && Math.abs(st - target) <= PROGRAMMATIC_TOLERANCE;
      if (isOurs) {
        lastProgrammaticTargetRef.current = null;
      } else if (st < lastScrollTopRef.current - 1) {
        // User moved UP by any means the intent listeners can't see
        // (scrollbar drag, momentum) — disengage.
        stickToBottomRef.current = false;
      }
      lastScrollTopRef.current = st;

      const contentBottom = getContentBottom(el);
      const at = contentBottom - el.clientHeight - st <= NEAR_BOTTOM_THRESHOLD;
      // Re-engage tail-follow only on the user's own return to the bottom.
      if (!isOurs && at) {
        stickToBottomRef.current = true;
      }
      setIsAtBottom(at);
    };

    el.addEventListener('wheel', onWheel, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('keydown', onKeyDown);
    el.addEventListener('scroll', onScroll, { passive: true });

    // Initialize position state from the mount position.
    lastScrollTopRef.current = el.scrollTop;
    const initialAt =
      getContentBottom(el) - el.clientHeight - el.scrollTop <=
      NEAR_BOTTOM_THRESHOLD;
    setIsAtBottom(initialAt);

    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('keydown', onKeyDown);
      el.removeEventListener('scroll', onScroll);
      if (idleTimer) {clearTimeout(idleTimer);}
    };
  }, []);

  // Tail-follow + pill truth on dep change (each streamed token, tool
  // result, etc.). When stuck: schedule ONE scroll for the next frame and
  // report at-bottom directly (computing from the pre-scroll position here
  // would flicker the pill every token). When not stuck: leave the view
  // alone and recompute the pill from the real position, since content
  // growth changes contentBottom without firing a scroll event.
  useEffect(() => {
    const el = ref.current;
    if (!el) {return;}
    if (stickToBottomRef.current) {
      setIsAtBottom(true);
      if (rafIdRef.current === null) {
        rafIdRef.current = requestAnimationFrame(() => {
          rafIdRef.current = null;
          const node = ref.current;
          if (node && stickToBottomRef.current) {
            performScrollToContentBottom(node);
          }
        });
      }
      return;
    }
    const distance = getContentBottom(el) - el.clientHeight - el.scrollTop;
    setIsAtBottom(distance <= NEAR_BOTTOM_THRESHOLD);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  // Cancel any pending frame on unmount.
  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {cancelAnimationFrame(rafIdRef.current);}
    };
  }, []);

  const scrollToBottom = useCallback(() => {
    const el = ref.current;
    if (!el) {return;}
    // User explicitly asked to follow again.
    stickToBottomRef.current = true;
    performScrollToContentBottom(el);
    setIsAtBottom(true);
  }, [performScrollToContentBottom]);

  // Pin a specific message to the top of the scroll container — used on
  // send so a freshly submitted user message snaps to the top of the
  // viewport (Claude.ai / ChatGPT pattern). The streaming assistant
  // response then fills the empty space below.
  //
  // Disengages tail-follow so subsequent text_delta / tool_result renders
  // don't pull the view down; the user re-engages by scrolling to the
  // bottom or clicking the pill. The pill itself keeps reflecting the real
  // content-vs-viewport truth via the dep-change effect above.
  const pinMessageToTop = useCallback((messageId: string, topOffset = 16) => {
    const el = ref.current;
    if (!el) {return;}
    const target = el.querySelector<HTMLElement>(
      `[data-chat-message-id="${messageId}"]`,
    );
    if (!target) {return;}
    const top = Math.max(0, target.offsetTop - el.offsetTop - topOffset);
    stickToBottomRef.current = false;
    lastProgrammaticTargetRef.current = top;
    el.scrollTop = top;
  }, []);

  return { ref, isAtBottom, scrollToBottom, pinMessageToTop };
}
